import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import {
  Alert,
  Anchor,
  Badge,
  Center,
  Drawer,
  Group,
  Loader,
  Stack,
  Table,
  Text,
} from '@mantine/core';

import { useProjectEconomicsDetail } from '@/hooks';
import {
  COST_BASIS_LABELS,
  COST_CATEGORY_LABELS,
  REGISTRY_FEE_SOURCE_LABELS,
  registryOrderBreakdown,
  type CostLine,
  type DisplayCurrency,
  type Money,
  type ProjectEconomics,
} from '@/types/economics';
import { providerLabel } from '@/types/provider-cost';
import { CATEGORY_COLORS, formatAmount } from './palette';
import classes from './Chart.module.css';

export interface ProjectEconomicsDrawerProps {
  row: ProjectEconomics | null;
  displayCurrency: DisplayCurrency;
  onClose: () => void;
}

/** The original amount, and how it became the display figure. */
function MoneyTrail({ money }: Readonly<{ money: Money }>) {
  const original = formatAmount(money.amount, money.currency);
  if (money.converted === null) {
    return (
      <Stack gap={0}>
        <Text size="sm">{original}</Text>
        <Text size="xs" c="dimmed">
          No exchange rate in the week before — not counted
        </Text>
      </Stack>
    );
  }
  if (money.converted.currency === money.currency) {
    return <Text size="sm">{original}</Text>;
  }
  return (
    <Stack gap={0}>
      <Text size="sm">{formatAmount(money.converted.amount, money.converted.currency)}</Text>
      <Text size="xs" c="dimmed">
        {original} at {money.converted.rate.toPrecision(6)} ({money.converted.source.toUpperCase()},{' '}
        {money.converted.rateDate})
      </Text>
    </Stack>
  );
}

/** The Armadillo orders behind a partner fee, each with its own fee, so the
 * lines add up to what is paid — or, for a fee an analyst set, say that they
 * need not. Fees are GBP, the only currency registry orders are billed in. */
function RegistryOrders({ line }: Readonly<{ line: CostLine }>) {
  const breakdown = registryOrderBreakdown(line);
  if (!breakdown) {
    return null;
  }
  return (
    <Table.Tr data-testid={`registry-orders-${line.id}`}>
      <Table.Td colSpan={4} pt={0}>
        <Stack gap={2} pl="lg">
          {breakdown.orders.map((order) => (
            <Group key={order.orderId} justify="space-between" wrap="nowrap" gap="sm">
              <Text size="xs">
                {order.companyName} · {order.products.join(', ')}
                {order.providerOrderId ? ` · order ${order.providerOrderId}` : ''}
              </Text>
              <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {REGISTRY_FEE_SOURCE_LABELS[order.feeSource] ?? order.feeSource} ·{' '}
                {formatAmount(order.fee, 'GBP')}
              </Text>
            </Group>
          ))}
          {breakdown.adjustment ? (
            <Group justify="space-between" wrap="nowrap" gap="sm">
              <Text size="xs">
                Adjustment{breakdown.adjustment.note ? `: ${breakdown.adjustment.note}` : ''}
              </Text>
              <Text size="xs" c="dimmed">
                {formatAmount(breakdown.adjustment.amount, 'GBP')}
              </Text>
            </Group>
          ) : null}
          {breakdown.feeFrom === 'analyst' ? (
            <Text size="xs" c="dimmed">
              Fee set by an analyst; the orders&apos; own prices are shown for reference.
            </Text>
          ) : null}
        </Stack>
      </Table.Td>
    </Table.Tr>
  );
}

function describe(line: CostLine): string {
  if (line.label) {
    return line.label;
  }
  const parts = [line.provider ? providerLabel(line.provider) : null, line.service].filter(Boolean);
  return parts.length ? parts.join(' · ') : COST_CATEGORY_LABELS[line.category];
}

/**
 * Every cost line behind one piece of work: what it was, how it was priced
 * (charged, quoted or from the rate card), and how it converted. Voided lines
 * are shown for the record and never summed.
 */
export function ProjectEconomicsDrawer({
  row,
  displayCurrency,
  onClose,
}: Readonly<ProjectEconomicsDrawerProps>) {
  const { detail, isLoading, error } = useProjectEconomicsDetail(
    row ? { source: row.source, sourceRef: row.sourceRef } : null,
    displayCurrency
  );

  return (
    <Drawer
      opened={row !== null}
      onClose={onClose}
      position="right"
      size="xl"
      title={row?.label ?? row?.projectCode ?? 'Work'}
    >
      {isLoading && !detail ? (
        <Center h={200}>
          <Loader />
        </Center>
      ) : null}
      {error ? <Alert color="red">{error.message}</Alert> : null}
      {detail ? (
        <Stack>
          <Group gap="lg">
            {detail.reportId ? (
              <Anchor component={Link} to={`/reports/${detail.reportId}`} size="sm">
                Open report
              </Anchor>
            ) : null}
            {detail.clientReference ? (
              <Text size="sm">Client case no. {detail.clientReference}</Text>
            ) : null}
            {detail.backofficeProjectId ? (
              <Text size="sm">Project ID {detail.backofficeProjectId}</Text>
            ) : null}
          </Group>
          <Group gap="xl">
            <Stack gap={0}>
              <Text size="xs" c="dimmed">
                Revenue
              </Text>
              {detail.revenue ? (
                <MoneyTrail money={detail.revenue} />
              ) : (
                <Text size="sm">Unpriced</Text>
              )}
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="dimmed">
                Cost
              </Text>
              <Text size="sm">{formatAmount(detail.totals.cost, displayCurrency)}</Text>
            </Stack>
            <Stack gap={0}>
              <Text size="xs" c="dimmed">
                Margin
              </Text>
              <Text size="sm" fw={600}>
                {formatAmount(detail.totals.margin, displayCurrency)}
              </Text>
            </Stack>
          </Group>
          {detail.costs.length === 0 ? (
            <Text size="sm" c="dimmed">
              No cost recorded for this work.
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Cost</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Priced</Table.Th>
                  <Table.Th ta="right">Amount</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {detail.costs.map((line) => (
                  <Fragment key={line.id}>
                    <Table.Tr
                      data-testid={`cost-line-${line.id}`}
                      style={line.voidedAt ? { opacity: 0.55 } : undefined}
                    >
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          <span
                            className={classes.swatch}
                            style={{ backgroundColor: CATEGORY_COLORS[line.category] }}
                          />
                          <Stack gap={0}>
                            <Text size="sm">{describe(line)}</Text>
                            <Text size="xs" c="dimmed">
                              {COST_CATEGORY_LABELS[line.category]}
                              {line.quantity
                                ? ` · ${Object.entries(line.quantity)
                                    .map(([unit, n]) => `${String(n)} ${unit}`)
                                    .join(', ')}`
                                : ''}
                            </Text>
                          </Stack>
                        </Group>
                      </Table.Td>
                      <Table.Td>{line.costDate}</Table.Td>
                      <Table.Td>
                        {line.voidedAt ? (
                          <Badge size="sm" color="gray" variant="light">
                            Voided
                          </Badge>
                        ) : line.basis ? (
                          <Text size="sm">{COST_BASIS_LABELS[line.basis]}</Text>
                        ) : (
                          <Badge size="sm" color="orange" variant="light">
                            Unrated
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">
                        {line.money ? (
                          <MoneyTrail money={line.money} />
                        ) : (
                          <Text size="xs" c="dimmed" maw={220}>
                            {line.unratedReason}
                          </Text>
                        )}
                        {line.voidReason ? (
                          <Text size="xs" c="dimmed">
                            {line.voidReason}
                          </Text>
                        ) : null}
                      </Table.Td>
                    </Table.Tr>
                    <RegistryOrders line={line} />
                  </Fragment>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      ) : null}
    </Drawer>
  );
}
